// src/pages/auth/LoginPage.tsx
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useState, type FormEvent } from 'react';
import * as authService from '@/lib/services/auth';
import { useAuth } from '@/context/AuthContext';
import { GlowLogo } from '@/components/ui/GlowLogo';
import { PasswordField } from '@/components/ui/PasswordField';

export default function LoginPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await authService.login(email, password);
      const user = await authService.getMe();
      setUser(user);
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <main
      className="ambient-bg min-h-screen flex items-center justify-center px-6"
    >
      <form
        onSubmit={onSubmit}
        className="screen-fade"
        style={{ width: '100%', maxWidth: 380, position: 'relative', zIndex: 2 }}
      >
        <header className="mb-12 text-center">
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <GlowLogo size="lg" />
          </div>
          <p
            className="mono-label"
            style={{ marginTop: 12, color: 'rgba(var(--fg-rgb),0.45)' }}
          >
            Operator Console
          </p>
        </header>

        <div className="space-y-6">
          <label className="block">
            <span className="mono-label" style={{ display: 'block', marginBottom: 6 }}>
              Email
            </span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="underline-input"
              placeholder="you@glow.rental"
            />
          </label>

          <PasswordField
            label="Password"
            required
            autoComplete="current-password"
            value={password}
            onChange={setPassword}
            className="underline-input"
            placeholder="••••••••••••"
          />
        </div>

        <div className="flex items-center justify-end mt-3">
          <Link
            to="/forgot-password"
            className="mono-label"
            style={{ color: 'rgba(var(--fg-rgb),0.45)' }}
          >
            Forgot password?
          </Link>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-pill"
          style={{ width: '100%', marginTop: 28, opacity: isLoading ? 0.5 : 1 }}
        >
          {isLoading ? 'Signing in…' : 'Sign In'}
        </button>

        {error && (
          <p
            className="mono-label"
            style={{ marginTop: 10, color: 'var(--color-error)', textAlign: 'center' }}
          >
            {error}
          </p>
        )}

        <footer
          className="mt-12 flex items-center justify-between mono-label"
          style={{ color: 'rgba(var(--fg-rgb),0.3)' }}
        >
          <span>V1.0.0-STABLE</span>
          <span className="flex items-center gap-1.5">
            <span className="led led-green" />
            ENC: AES-256-GCM
          </span>
        </footer>
      </form>
    </main>
  );
}
